import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { scenes } from '@padcanvas/shared';
import { scenesApi, SCENES_QUERY_KEY, type SceneListFilters } from './scenesApi';
import {
    AGREEMENTS, AGREEMENT_MARK, BENCH_MODULES, CANNOT_LABEL, CONFIDENCES, TRUSTS, useModuleLabel,
} from './sceneLabels';
import { AgreementMarks, ConfidencePill, TrustPill } from './ScenePills';
import './Scenes.css';

/**
 * Admin → Scenes, the chapter view: how far a bench's chapter is compiled, how
 * much of it can be trusted, and the list a reviewer works through.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md §10.
 */

const CONFIDENCE_RANK: Record<string, number> = { low: 0, medium: 1, high: 2 };

export function Scenes() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const moduleLabel = useModuleLabel();

    const moduleId = params.get('module') ?? BENCH_MODULES[0]?.moduleId ?? '';
    const filters: SceneListFilters = {
        trust: (params.get('trust') || undefined) as SceneListFilters['trust'],
        outcome: (params.get('outcome') || undefined) as SceneListFilters['outcome'],
        confidence: (params.get('confidence') || undefined) as SceneListFilters['confidence'],
        agreement: params.get('agreement') === 'disagrees' ? 'disagrees' : undefined,
    };

    const setParam = (key: string, value: string | undefined) => setParams(prev => {
        const p = new URLSearchParams(prev);
        if (value) p.set(key, value); else p.delete(key);
        return p;
    }, { replace: true });

    const coverage = useQuery({
        queryKey: [SCENES_QUERY_KEY, 'coverage', moduleId],
        queryFn: () => scenesApi.coverage(moduleId),
        enabled: !!moduleId,
    });

    const list = useQuery({
        queryKey: [SCENES_QUERY_KEY, 'list', moduleId, filters],
        queryFn: () => scenesApi.list(moduleId, filters),
        enabled: !!moduleId,
        placeholderData: keepPreviousData,
    });

    // The review queue works from least sure to most: low confidence first,
    // then medium, high, and last the `cannot`s, which carry no confidence.
    const rows = useMemo(() => [...(list.data?.items ?? [])].sort((a, b) =>
        (CONFIDENCE_RANK[a.confidence ?? ''] ?? 3) - (CONFIDENCE_RANK[b.confidence ?? ''] ?? 3)
        || b.updatedTs - a.updatedTs), [list.data]);

    const bench = BENCH_MODULES.find(m => m.moduleId === moduleId)?.bench;

    return (
        <div className="scn-page">
            <div className="page-header">
                <h1>Scenes</h1>
                <p className="scn-dim">
                    Questions set up on a bench, solved by its engine and checked against the answer key.
                </p>
            </div>

            <div className="scn-toolbar">
                <label>
                    Chapter
                    <select value={moduleId} onChange={e => setParams({ module: e.target.value }, { replace: true })}>
                        {scenes.allBenches().map(b => (
                            <optgroup key={b.bench} label={b.title}>
                                {b.covers.map(m => <option key={m} value={m}>{moduleLabel(m)}</option>)}
                            </optgroup>
                        ))}
                    </select>
                </label>
                {bench && <span className="scn-dim">bench: {bench}</span>}
            </div>

            {coverage.isLoading && <p className="scn-dim">Loading coverage…</p>}
            {coverage.error && <p className="scn-error">{(coverage.error as Error).message}</p>}
            {coverage.data && <Coverage c={coverage.data} />}

            <div className="scn-toolbar">
                <label>
                    Trust
                    <select value={filters.trust ?? ''} onChange={e => setParam('trust', e.target.value)}>
                        <option value="">any</option>
                        {TRUSTS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </label>
                <label>
                    Outcome
                    <select value={filters.outcome ?? ''} onChange={e => setParam('outcome', e.target.value)}>
                        <option value="">any</option>
                        <option value="scene">scene</option>
                        <option value="cannot">cannot</option>
                    </select>
                </label>
                <label>
                    Confidence
                    <select value={filters.confidence ?? ''} onChange={e => setParam('confidence', e.target.value)}>
                        <option value="">any</option>
                        {CONFIDENCES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label className="scn-check">
                    <input
                        type="checkbox"
                        checked={filters.agreement === 'disagrees'}
                        onChange={e => setParam('agreement', e.target.checked ? 'disagrees' : undefined)}
                    />
                    Disagreements only
                </label>
                <span className="scn-dim">
                    {list.isFetching ? 'Loading…' : `${rows.length} question${rows.length === 1 ? '' : 's'}`}
                </span>
            </div>

            {list.error && <p className="scn-error">{(list.error as Error).message}</p>}

            <div className="dt-scroll">
                <div className="dt-container">
                    <table className="dt scn-table">
                        <thead>
                            <tr>
                                <th>Question</th>
                                <th>Outcome</th>
                                <th>Confidence</th>
                                <th>Trust</th>
                                <th>Agreement</th>
                                <th>Stale</th>
                                <th>Review</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr
                                    key={r.questionId}
                                    className="scn-row"
                                    onClick={() => navigate(`/scenes/${encodeURIComponent(r.questionId)}`)}
                                >
                                    <td className="scn-preview">{r.questionPreview}</td>
                                    <td>
                                        {r.outcome === 'scene'
                                            ? 'scene'
                                            : <span className="scn-cannot">cannot · {r.reason ? CANNOT_LABEL[r.reason] : '?'}</span>}
                                    </td>
                                    <td><ConfidencePill confidence={r.confidence} /></td>
                                    <td><TrustPill trust={r.trust} /></td>
                                    <td><AgreementMarks agreement={r.agreement} /></td>
                                    <td>{r.stale ? <span className="scn-stale">stale</span> : ''}</td>
                                    <td>{r.reviewed ? (r.reviewed === 'accept' ? 'accepted' : 'rejected') : <span className="scn-dim">—</span>}</td>
                                </tr>
                            ))}
                            {!list.isFetching && rows.length === 0 && (
                                <tr><td colSpan={7} className="scn-dim">No scenes match.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Coverage({ c }: { c: scenes.SceneCoverage }) {
    const n = (x: number | undefined) => x ?? 0;
    return (
        <div className="scn-coverage">
            <section className="scn-card">
                <h3>Compiled</h3>
                <dl className="scn-dl">
                    <dt>Questions</dt><dd>{c.questions}</dd>
                    <dt>Compiled</dt><dd>{c.compiled}</dd>
                    <dt>Stale</dt><dd className={c.stale ? 'scn-stale' : ''}>{c.stale}</dd>
                </dl>
            </section>
            <section className="scn-card">
                <h3>Outcome</h3>
                <dl className="scn-dl">
                    <dt>Scene</dt><dd>{n(c.byOutcome.scene)}</dd>
                    {scenes.CANNOT_REASONS.map(r => (
                        <FragmentRow key={r} label={`cannot · ${CANNOT_LABEL[r]}`} value={n(c.byOutcome[r])} />
                    ))}
                </dl>
            </section>
            <section className="scn-card">
                <h3>Trust</h3>
                <dl className="scn-dl">
                    {TRUSTS.map(t => <FragmentRow key={t} label={<TrustPill trust={t} />} value={n(c.byTrust[t])} />)}
                </dl>
            </section>
            <section className="scn-card">
                <h3>Asks vs key</h3>
                <dl className="scn-dl">
                    {AGREEMENTS.map(a => (
                        <FragmentRow key={a} label={<><span className={`scn-mark--${a}`}>{AGREEMENT_MARK[a]}</span> {a}</>} value={n(c.byAgreement[a])} />
                    ))}
                </dl>
            </section>
            <section className="scn-card">
                <h3>Calibration</h3>
                <table className="scn-calib">
                    <thead><tr><th>Confidence</th><th>Scenes</th><th>Verified</th><th>Rejected</th></tr></thead>
                    <tbody>
                        {CONFIDENCES.map(k => {
                            const row = c.byConfidence[k];
                            return (
                                <tr key={k}>
                                    <td><ConfidencePill confidence={k} /></td>
                                    <td>{row?.scenes ?? 0}</td>
                                    <td>{row?.verified ?? 0}</td>
                                    <td>{row?.rejected ?? 0}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>
        </div>
    );
}

function FragmentRow({ label, value }: { label: React.ReactNode; value: number }) {
    return <><dt>{label}</dt><dd className={value ? '' : 'scn-dim'}>{value}</dd></>;
}
